import axios from 'axios';
import { captureException } from '@sentry/nextjs';

export interface AnonymousSessionRequest {
  device_id: string;
  device_name: string;
  device_type: 'web' | 'mobile' | 'tablet';
  app_version?: string;
}

export interface AnonymousSessionResponse {
  session_id: string;
  anonymous_token: string;
  expires_at: string;
  created_at: string;
}

export class AnonymousSessionService {
  private readonly apiClient;
  private currentSession: AnonymousSessionResponse | null = null;

  constructor(private readonly baseUrl: string) {
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load existing session from storage
    this.loadSessionFromStorage();
  }

  async createAnonymousSession(
    request?: Partial<AnonymousSessionRequest>
  ): Promise<AnonymousSessionResponse> {
    // Check if we have a valid existing session
    if (this.currentSession && !this.isSessionExpired()) {
      return this.currentSession;
    }

    const sessionRequest: AnonymousSessionRequest = {
      device_id: request?.device_id || this.generateDeviceId(),
      device_name: request?.device_name || this.generateDeviceName(),
      device_type: request?.device_type || this.detectDeviceType(),
      app_version: request?.app_version || '1.0.0',
    };

    const response = await this.apiClient.post(
      '/v1/auth/anonymous-session',
      sessionRequest
    );

    const session: AnonymousSessionResponse = {
      session_id: response.data.session_id,
      anonymous_token: response.data.anonymous_token,
      expires_at: response.data.expires_at,
      created_at: response.data.created_at,
    };

    this.currentSession = session;
    this.saveSessionToStorage(session);

    return session;
  }

  getCurrentSession(): AnonymousSessionResponse | null {
    if (this.currentSession && this.isSessionExpired()) {
      this.clearSession();
      return null;
    }
    return this.currentSession;
  }

  getAnonymousToken(): string | null {
    const session = this.getCurrentSession();
    return session ? session.anonymous_token : null;
  }

  isSessionExpired(): boolean {
    if (!this.currentSession) return true;

    const expiryDate = new Date(this.currentSession.expires_at);
    const now = new Date();

    // Consider session expired if it expires within the next 5 minutes
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    return expiryDate <= fiveMinutesFromNow;
  }

  clearSession(): void {
    this.currentSession = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('motito_anonymous_session');
    }
  }

  async refreshSession(): Promise<AnonymousSessionResponse> {
    this.clearSession();
    return await this.createAnonymousSession();
  }

  // Utility methods for tracking and analytics
  getSessionDuration(): number {
    if (!this.currentSession) return 0;

    const createdAt = new Date(this.currentSession.created_at);
    const now = new Date();
    return now.getTime() - createdAt.getTime();
  }

  getTimeUntilExpiry(): number {
    if (!this.currentSession) return 0;

    const expiryDate = new Date(this.currentSession.expires_at);
    const now = new Date();
    return Math.max(0, expiryDate.getTime() - now.getTime());
  }

  isSessionActive(): boolean {
    return this.currentSession !== null && !this.isSessionExpired();
  }

  getSessionInfo(): {
    isActive: boolean;
    sessionId: string | null;
    timeUntilExpiry: number;
    sessionDuration: number;
  } {
    return {
      isActive: this.isSessionActive(),
      sessionId: this.currentSession?.session_id || null,
      timeUntilExpiry: this.getTimeUntilExpiry(),
      sessionDuration: this.getSessionDuration(),
    };
  }

  // Method to ensure we have a valid session for guest users
  async ensureValidSession(): Promise<string> {
    let session = this.getCurrentSession();

    if (!session || this.isSessionExpired()) {
      session = await this.createAnonymousSession();
    }

    return session.anonymous_token;
  }

  private saveSessionToStorage(session: AnonymousSessionResponse): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('motito_anonymous_session', JSON.stringify(session));
    }
  }

  private loadSessionFromStorage(): void {
    if (typeof window !== 'undefined') {
      try {
        const sessionStr = localStorage.getItem('motito_anonymous_session');
        if (sessionStr) {
          const session = JSON.parse(sessionStr);
          // Validate the session structure
          if (
            session.session_id &&
            session.anonymous_token &&
            session.expires_at
          ) {
            this.currentSession = session;
          }
        }
      } catch (error) {
        captureException(error);
        // Clear invalid session data
        localStorage.removeItem('motito_anonymous_session');
      }
    }
  }

  private generateDeviceId(): string {
    if (typeof window !== 'undefined') {
      let deviceId = localStorage.getItem('motito_device_id');
      if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        localStorage.setItem('motito_device_id', deviceId);
      }
      return deviceId;
    }
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateDeviceName(): string {
    if (typeof window === 'undefined') return 'Server';

    const userAgent = window.navigator.userAgent;

    // Extract browser name
    let browserName = 'Unknown Browser';
    if (userAgent.includes('Chrome')) browserName = 'Chrome';
    else if (userAgent.includes('Firefox')) browserName = 'Firefox';
    else if (userAgent.includes('Safari')) browserName = 'Safari';
    else if (userAgent.includes('Edge')) browserName = 'Edge';

    // Extract OS
    let osName = 'Unknown OS';
    if (userAgent.includes('Windows')) osName = 'Windows';
    else if (userAgent.includes('Mac OS')) osName = 'macOS';
    else if (userAgent.includes('Linux')) osName = 'Linux';
    else if (userAgent.includes('Android')) osName = 'Android';
    else if (userAgent.includes('iOS')) osName = 'iOS';

    return `${browserName} on ${osName}`;
  }

  private detectDeviceType(): 'web' | 'mobile' | 'tablet' {
    if (typeof window === 'undefined') return 'web';

    const userAgent = window.navigator.userAgent.toLowerCase();

    if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
      return 'tablet';
    }

    if (
      userAgent.includes('mobile') ||
      userAgent.includes('android') ||
      userAgent.includes('iphone')
    ) {
      return 'mobile';
    }

    return 'web';
  }
}
